<template>
    <div>
        <div class="w-full text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions['costing_assumption_driver_add'])"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openEditMonthlySalary">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>
        <div class="w-full flex gap-5 justify-between mt-5">
            <hts-form-select
                v-model="assumptionDriverSelected.id"
                style="max-width: 400px"
                :text="assumptionDriverSelected.name"
                :items="assumptionDrivers"
                :clearable="false"
                key-id="_id"
                key-label="name" />

            <hts-form-button
                v-if="checkPermission(layoutPermissions['costing_assumption_driver_edit'])"
                color="main"
                icon-name="hi-pencil-square-icon"
                icon-position="right"
                type="submit"
                @click="handleEditAssumptionDriver">
                {{ $t('labels.edit') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            class="mt-5"
            :headers="assumptionCostingHeaders"
            :items="costAssumptionDrivers" />

        <add-assumption-driver
            ref="addAssumptionDriver"
            @refresh-tables="getData(true)" />
        <edit-assumption-driver
            ref="editAssumptionDriver"
            @refresh-tables="getData(true)" />
        <hts-modal
            :show="showPopupDeleteAssumptionDriver"
            :title="$t('labels.deleteAssumptionDriverCosting')"
            type="confirm"
            :content="$t('labels.confirmDeleteAssumptionDriverCosting')"
            @hide="showPopupDeleteAssumptionDriver = false"
            @confirm="actionDeleteAssumptionDriver" />
    </div>
</template>

<script setup lang="ts">
import AddAssumptionDriver from './-component/assumption-driver/add.vue'
import EditAssumptionDriver from './-component/assumption-driver/edit.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import { numberFormatter } from '~/pages/trucker-[id]/price/vendor/ignore/data'

const { $formatters } = useNuxtApp()

const dataTables = ref()

const items = ref({})
const showPopupDeleteAssumptionDriver = ref(false)
const assumptionDriverIdSelectedForDelete = ref('')
const apiListAssumptionDriver = $url.cost.assumptionCosting.assumptionCostingList
const assumptionDrivers = ref<any[]>([])
const costAssumptionDrivers = ref<any[]>([])
const assumptionDriverSelected = ref({ id: '', name: '' })

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const editAssumptionDriver: any = ref(null)
const handleEditAssumptionDriver = () => {
  if (editAssumptionDriver.value) {
    editAssumptionDriver.value.open(assumptionDriverSelected.value.id)
  }
}

const assumptionCostingHeaders = [
  {
    field: 'from',
    headerName: $t('labels.fromKm'),
    sortable: true,
    valueGetter: (params: any) => {
      return typeof params.data.from === 'number' ? numberFormatter(params.data.from) : ''
    }
  },
  {
    field: 'to',
    headerName: $t('labels.toKm'),
    sortable: true,
    valueGetter: (params: any) => {
      return typeof params.data.to === 'number' ? numberFormatter(params.data.to) : ''
    }
  },
  {
    field: 'shipmentSalary',
    headerName: $t('labels.shipmentSalary'),
    sortable: true,
    valueGetter: (params: any) => {
      return typeof params.data.shipmentSalary === 'number' ? numberFormatter(params.data.shipmentSalary) : ''
    }
  },
  {
    field: 'incurredSalary',
    headerName: $t('labels.incurredSalaryKm'),
    sortable: true,
    valueGetter: (params: any) => {
      return typeof params.data.incurredSalary === 'number' ? numberFormatter(params.data.incurredSalary) : ''
    }
  }
]

onBeforeMount(async () => {
  await getData()
})

const addAssumptionDriver: any = ref(null)
const openEditMonthlySalary = (cooperator: any) => {
  if (addAssumptionDriver.value) {
    addAssumptionDriver.value.open()
  }
}
const actionDeleteAssumptionDriver = () => {
  const api = $url.cost.assumptionCosting.deleteAssumptionDriver

  $api(api, { uriParams: { ID: assumptionDriverIdSelectedForDelete.value } }).then((data: any) => {
    const resData: any = data.data.value

    if (resData?.success) {
      assumptionDriverIdSelectedForDelete.value = ''
      refreshTables()
      showPopupDeleteAssumptionDriver.value = false
      process.client && $toast().success($t('labels.deleteSuccess', { name: $t('labels.assumptionDriverCosting') }))
    }
  })
}

const getData = async (reloadCost?: boolean) => {
  const url = $url.cost.assumptionCosting.getAll
  const data: any = await $api(url)
  const resData = data?.data?.value

  if (resData?.success) {
    assumptionDrivers.value = resData.data
    if (!assumptionDriverSelected?.value?.id) {
      assumptionDriverSelected.value.id = resData.data[0]._id || ''
      assumptionDriverSelected.value.name = resData.data[0].name || ''
      return
    }

    if (reloadCost) {
      await getCostAssumptionDrivers()
    }
  }
}

watch(
  () => assumptionDriverSelected.value.id,
  async (val) => {
    await getCostAssumptionDrivers()
  },
  { deep: true }
)

const getCostAssumptionDrivers = async () => {
  const url = $url.cost.assumptionCosting.getDetailAssumptionDriver
  const id = assumptionDriverSelected.value.id

  if (!id) {
    return
  }

  const data: any = await $api(url, { uriParams: { ID: id } })
  const resData = data?.data?.value
  if (resData?.success) {
    const result: any = []
    resData.data.costingInformation.forEach((cost: any, index: number) => {
      cost.id = index
      result.push(cost)
    })
    costAssumptionDrivers.value = result
  }
  refreshTables()
}
</script>
